import React, { useEffect, useCallback, useState } from "react";
import { UIContext } from "../hooks/useProviderContext/useProviderContext";
import Loader from "../components/Loader/Loader";
import { isValidUUID } from "../lib/utils";

interface UIProviderProps {
  businessId: string;
  bookezToken: string; // Business token from the software
  theme: "dark" | "light" | "";
  plaidClientUserId?: string; // Optional user ID for client-specific operations
  plaidClientUserName?: string; // Optional user token for client-specific operations
  children: React.ReactNode;
  onError?: (error: Error) => void;
  onSuccess?: (
    apiType: "GET" | "POST" | "DOWNLOAD" | "OPTIONS",
    apiName: string,
    message: string
  ) => void;
}

const UIProvider: React.FC<UIProviderProps> = ({
  businessId,
  bookezToken,
  plaidClientUserId,
  plaidClientUserName,
  theme,
  children,
  onError,
  onSuccess,
}) => {
  const [isValidated, setIsValidated] = useState(false);
  const handleError = useCallback(
    (message: string | Error) => {
      const errorObj = message instanceof Error ? message : new Error(message);
      // setError(errorObj); // Set the error in state
      onError?.(errorObj); // Propagate to parent if onError is provided
    },
    [onError]
  );

  // Handle success messages
  const handleSuccess = useCallback(
    (
      apiType: "GET" | "POST" | "DOWNLOAD" | "OPTIONS",
      apiName: string,
      message: string
    ) => {
      onSuccess?.(apiType, apiName, message); // Propagate success message to parent if onSuccess is provided
    },
    [onSuccess]
  );

  // Simple validation to check if there's a token
  useEffect(() => {
    const validateProvidedId = () => {
      setIsValidated(false);
      // setError(null);
      if (!businessId || !isValidUUID(businessId)) {
        onError?.(new Error("Invalid Business ID format or empty ID."));
        return;
      }

      if (!bookezToken) {
        onError?.(new Error("Bookez token is required for authentication."));
        return;
      }
      setIsValidated(true);
    };

    validateProvidedId();
  }, [businessId, bookezToken, handleError]);

  if (!isValidated) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        <Loader />
      </div>
    );
  }

  return (
    <UIContext.Provider
      value={{
        businessId,
        bookezToken,
        plaidClientUserId,
        plaidClientUserName,
        theme,
        onError: handleError,
        onSuccess: handleSuccess,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};

export { UIContext, UIProvider };
